/**
 * Галерея фотоотчёта в статье блога.
 *
 * Раскладка журнальная: hero во всю ширину сверху, дальше justified-ряды,
 * и каждый N-й кадр — крупной врезкой во всю ширину (задаёт ритм при скролле).
 * Лайтбокс — PhotoSwipe, который уже есть в теме (window.theme.LoadPhotoswipe),
 * той же механикой, что и зум товара (assets/zoom.js).
 */
(function () {
  'use strict';

  var ITEM = '[data-gallery-item]';
  var DOWNLOAD = '[data-gallery-download]';

  var GAP = 6;
  var GAP_MOBILE = 4;
  var ROW_HEIGHT = 300;
  var ROW_HEIGHT_MOBILE = 170;
  // Ритм крупных кадров задаётся в snippets/article-gallery.liquid (data-feature)
  var HERO_MAX_VH = 0.8;
  var FEATURE_MAX_VH = 0.72;

  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function fileNameFrom(url) {
    try {
      var path = new URL(url, window.location.href).pathname;
      var name = path.substring(path.lastIndexOf('/') + 1);
      return name || 'photo.jpg';
    } catch (e) {
      return 'photo.jpg';
    }
  }

  /**
   * Фото отдаёт cdn.shopify.com — это кросс-домен, поэтому атрибут download
   * браузер игнорирует и картинка просто откроется. Тянем как blob и отдаём
   * объектной ссылкой. Если CORS не дал — честно открываем оригинал в новой
   * вкладке, чтобы кнопка не «молчала».
   */
  function downloadImage(url) {
    var name = fileNameFrom(url);

    fetch(url, { mode: 'cors' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.blob();
      })
      .then(function (blob) {
        var objectUrl = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = objectUrl;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(function () {
          URL.revokeObjectURL(objectUrl);
        }, 1000);
      })
      .catch(function () {
        window.open(url, '_blank', 'noopener');
      });
  }

  // Кнопка живёт в подписи PhotoSwipe и перерисовывается на каждом слайде,
  // поэтому слушаем делегированно.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest ? event.target.closest(DOWNLOAD) : null;
    if (!btn) return;
    event.preventDefault();
    downloadImage(btn.getAttribute('href'));
  });

  var ICON =
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"/></svg>';

  if (!customElements.get('article-gallery')) {
    customElements.define(
      'article-gallery',
      class extends HTMLElement {
        constructor() {
          super();
          this.tiles = Array.prototype.slice.call(this.querySelectorAll(ITEM));
          this.grid = this.querySelector('.article-gallery__grid');
          this.downloadLabel = this.dataset.downloadLabel || 'Download';
        }

        connectedCallback() {
          this.tiles.forEach(
            function (tile, index) {
              tile.addEventListener(
                'click',
                function (event) {
                  event.preventDefault();
                  this.open(index);
                }.bind(this)
              );
            }.bind(this)
          );

          this.layout();
          this.initReveal();

          var self = this;
          var raf = null;
          this.onResize = function () {
            if (raf) window.cancelAnimationFrame(raf);
            raf = window.requestAnimationFrame(function () {
              self.layout();
            });
          };
          window.addEventListener('resize', this.onResize);
        }

        disconnectedCallback() {
          if (this.onResize) window.removeEventListener('resize', this.onResize);
          if (this.observer) this.observer.disconnect();
        }

        aspectOf(el) {
          var w = parseInt(el.dataset.width, 10);
          var h = parseInt(el.dataset.height, 10);
          return w && h ? w / h : 1;
        }

        /**
         * Крупный кадр (hero / врезка) во всю ширину.
         *
         * Высоту берём из пропорций, но упираем в потолок, чтобы портретный кадр
         * не занял три экрана. ВАЖНО: при упоре НЕ обрезаем — вместо этого кадр
         * остаётся целым (object-fit: contain) и центрируется. Обрезка кадров —
         * ровно то, чего мы избегаем: люди ищут на фото себя.
         */
        placeFeature(el, aspect, width, maxVh) {
          var maxHeight = window.innerHeight * maxVh;
          var height = Math.min(width / aspect, maxHeight);

          el.classList.add('article-gallery__item--feature');
          el.style.flex = '0 0 100%';
          el.style.width = '100%';
          el.style.height = height + 'px';
        }

        /**
         * Justified-раскладка (как у Flickr/Google Photos).
         *
         * CSS-трюк на flex-grow даёт лишь ПРИБЛИЗИТЕЛЬНЫЕ ширины: flex раздаёт
         * остаток пропорционально, из-за чего плитка не совпадает с кадром и
         * object-fit: cover срезает края. Поэтому считаем точно:
         *   высота ряда = (ширина контейнера − зазоры) / сумма пропорций
         *   ширина плитки = пропорция × высота ряда
         * Тогда плитка = кадр один в один, и обрезать нечего.
         */
        layout() {
          if (!this.grid) return;

          var width = this.grid.clientWidth;
          if (!width) return;

          var self = this;
          var gap = isMobile() ? GAP_MOBILE : GAP;
          var target = isMobile() ? ROW_HEIGHT_MOBILE : ROW_HEIGHT;

          var row = [];
          var aspectSum = 0;

          function flush(fit) {
            if (!row.length) return;
            // fit=true — ряд заполнен, подгоняем высоту под ширину контейнера.
            // fit=false — последний неполный ряд, оставляем целевую высоту.
            var height = fit ? (width - (row.length - 1) * gap) / aspectSum : target;

            row.forEach(function (entry) {
              entry.el.style.flex = '0 0 auto';
              entry.el.style.width = entry.aspect * height + 'px';
              entry.el.style.height = height + 'px';
            });

            row = [];
            aspectSum = 0;
          }

          this.tiles.forEach(function (el) {
            var aspect = self.aspectOf(el);
            // Ритм задан в разметке (Liquid) — от него зависят srcset/sizes.
            // Здесь его не пересчитываем, иначе источники правды разъедутся.
            var feature = el.dataset.feature;

            // Крупный кадр разрывает ряд и встаёт на свою строку
            if (feature) {
              flush(false);
              self.placeFeature(
                el,
                aspect,
                width,
                feature === 'hero' ? HERO_MAX_VH : FEATURE_MAX_VH
              );
              return;
            }

            row.push({ el: el, aspect: aspect });
            aspectSum += aspect;

            // Ряд перерос ширину контейнера — фиксируем его
            if (aspectSum * target + (row.length - 1) * gap >= width) flush(true);
          });

          flush(false);

          this.grid.classList.add('is-measured');
        }

        /** Плавное появление при скролле. */
        initReveal() {
          if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
            this.tiles.forEach(function (el) {
              el.classList.add('is-visible');
            });
            return;
          }

          this.grid.classList.add('has-reveal');

          this.observer = new IntersectionObserver(
            function (entries, obs) {
              entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('is-visible');
                obs.unobserve(entry.target);
              });
            },
            { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
          );

          var obs = this.observer;
          this.tiles.forEach(function (el) {
            obs.observe(el);
          });
        }

        open(index) {
          if (!window.theme || !window.theme.LoadPhotoswipe) return;

          var slides = this.tiles.map(function (tile) {
            var img = tile.querySelector('img');
            return {
              src: tile.dataset.src,
              w: parseInt(tile.dataset.width, 10),
              h: parseInt(tile.dataset.height, 10),
              // превью как заглушка, пока грузится оригинал
              msrc: img ? img.currentSrc || img.src : tile.dataset.src,
            };
          });

          var self = this;
          var options = {
            history: false,
            focus: false,
            index: index,
            // ВАЖНО: вся раскладка лайтбокса в теме (фон, стрелки, контейнер —
            // 80+ правил) привязана к её классу pswp-zoom-gallery. Без него
            // кнопки сваливаются неоформленными в угол. Свой класс добавляем
            // вторым — только для отличий галереи.
            mainClass:
              'pswp-zoom-gallery pswp-article-gallery' +
              (this.tiles.length === 1 ? ' pswp-zoom-gallery--single' : ''),
            showHideOpacity: true,
            closeOnScroll: false,
            closeOnVerticalDrag: true,
            captionEl: true,
            closeEl: true,
            closeElClasses: ['caption-close', 'title'],
            tapToClose: false,
            clickToCloseNonZoomable: false,
            maxSpreadZoom: 2,
            loop: true,
            spacing: 0,
            allowPanToNext: true,
            pinchToClose: false,
            addCaptionHTMLFn: function (item, captionEl) {
              captionEl.children[0].innerHTML =
                '<a class="article-gallery__download" data-gallery-download href="' +
                item.src +
                '">' +
                ICON +
                '<span>' +
                self.downloadLabel +
                '</span></a>';
              return true;
            },
            // Открытие/закрытие анимируется от плитки, по которой кликнули
            getThumbBoundsFn: function (i) {
              var tile = self.tiles[i];
              if (!tile) return null;
              var rect = tile.getBoundingClientRect();
              var scrollY = window.scrollY || document.documentElement.scrollTop;
              return { x: rect.left, y: rect.top + scrollY, w: rect.width };
            },
          };

          new window.theme.LoadPhotoswipe(slides, options);

          if (window.a11y) {
            window.a11y.lastElement = this.tiles[index];
          }
        }
      }
    );
  }
})();
